import JsonLd from "@/components/common/JsonLd";
import useSeo from "@/hooks/useSeo";

const stats = [
  { value: "500万+", label: "注册读者" },
  { value: "12000+", label: "收录作品" },
  { value: "3000万", label: "月均访问" },
  { value: "98%", label: "读者满意度" },
];

const values = [
  { icon: "ri-file-list-3-line", title: "海量资源", desc: "收录上万部热血、恋爱、奇幻、悬疑等多分类漫画，满足不同读者的阅读喜好。" },
  { icon: "ri-flashlight-fill", title: "每日更新", desc: "连载热门作品每日同步更新，新作第一时间上架，完结佳作完整收录。" },
  { icon: "ri-shield-check-line", title: "阅读体验", desc: "界面简洁清爽、分类清晰，支持按人气、评分、更新排序，快速定位想看的漫画。" },
  { icon: "ri-user-heart-line", title: "口碑服务", desc: "注重读者反馈，持续优化阅读体验，98% 的读者满意度见证我们的坚持。" },
];

const contacts = [
  { icon: "ri-mail-line", label: "邮箱", value: "service@91mh.com" },
  { icon: "ri-phone-line", label: "客服热线", value: "400-888-9110" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "91漫画" },
  { icon: "ri-time-line", label: "服务时间", value: "每日 9:00 - 22:00" },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们",
    description:
      "91漫画是一个致力于提供海量免费漫画在线阅读的平台，涵盖多分类热门作品、人气榜与完结榜，凭借丰富的资源和优质的阅读体验赢得读者信赖。",
    keywords: "91漫画,关于我们,91漫画品牌介绍,91漫画口碑,91漫画联系方式",
    path: "/about",
  });

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "关于91漫画",
          url: "https://panelset.github.io/about",
          description: "91漫画是海量免费漫画在线阅读平台，涵盖多分类热门作品、人气榜与完结榜。",
          inLanguage: "zh-CN",
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "91漫画",
          url: "https://panelset.github.io/",
          email: "service@91mh.com",
          telephone: "400-888-9110",
          foundingDate: "2019",
          description: "海量免费漫画在线阅读平台，分类浏览、人气榜、热门推荐、完结榜与新作。",
        }}
      />

      {/* hero */}
      <section className="pt-24 pb-12 bg-gradient-to-b from-primary-100/70 to-background-50">
        <div className="mx-auto px-4 md:px-6 max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-sm">
            <i className="ri-community-line"></i>
            关于我们
          </span>
          <h1 className="mt-5 text-3xl md:text-5xl font-heading text-foreground-950">让每个人都能轻松读到好漫画</h1>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
            91漫画成立于2019年，是一家专注于漫画在线阅读的内容平台。我们致力于汇集各分类优质漫画，
            通过清晰的分级与人性化的搜索，让读者在几秒钟内找到心仪的作品，畅享阅读乐趣。
          </p>
        </div>
      </section>

      {/* stats */}
      <section className="py-8 bg-background-50">
        <div className="mx-auto px-4 md:px-6 max-w-6xl">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {stats.map((s) => (
              <div key={s.label} className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
                <p className="text-3xl md:text-4xl font-heading text-primary-600">{s.value}</p>
                <p className="mt-2 text-sm text-foreground-600">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* brand story */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="mx-auto px-4 md:px-6 max-w-6xl">
          <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20and%20cozy%20illustration%20of%20a%20reading%20corner%20with%20stacked%20colorful%20manga%20books%2C%20open%20comics%20and%20a%20lamp%20glow%2C%20coral%20and%20cream%20palette%2C%20storytelling%20and%20comfortable%20atmosphere%2C%20high%20detail&width=800&height=600&seq=about-2026-x1&orientation=landscape"
              alt="91漫画品牌故事阅读场景"
              title="91漫画品牌故事"
              className="w-full lg:w-1/2 rounded-lg object-cover object-top border border-background-200/70"
            />
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950">品牌故事</h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
                从最初一个小小的阅读兴趣社区，到如今服务数百万读者的漫画平台，91漫画始终坚守一个朴素的心愿——
                让好看的漫画被更多人看见，让爱看漫画的人更容易找到心头好。
              </p>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-700">
                我们相信，无论热血、恋爱、奇幻还是悬疑，每一部好作品都值得被认真对待。正因如此，
                我们不断优化分类体系、完善人气榜与完结榜，让阅读体验更加顺畅、贴心。
              </p>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-700">
                未来，91漫画将继续与优秀的创作者携手，带来更多精彩内容，陪伴每一位读者的闲暇时光。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* values */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="mx-auto px-4 md:px-6 max-w-6xl">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 text-center mb-8">为什么选择91漫画</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((v) => (
              <div key={v.title} className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                <span className="flex items-center justify-center w-14 h-14 rounded-full bg-accent-100 text-accent-700 text-2xl">
                  <i className={v.icon}></i>
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-900">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* contact */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="mx-auto px-4 md:px-6 max-w-5xl">
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-950">联系我们</h2>
            <p className="mt-3 text-sm text-foreground-600">有任何建议或问题，欢迎随时与我们取得联系</p>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {contacts.map((c) => (
              <div key={c.label} className="flex flex-col items-center text-center gap-3 p-6 rounded-lg bg-background-100 border border-background-200/70">
                <span className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 text-primary-600 text-xl">
                  <i className={c.icon}></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">{c.label}</p>
                  <p className="mt-1 text-sm font-medium text-foreground-800">{c.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}